import { useMutation, useQueryClient } from "@tanstack/react-query";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import useGPTUser, { PinnedChats } from "./useBrieGPTUser";

export interface UpdateBrieGPTUserPayload {
  PinnedChat: PinnedChats;
}

interface Props {
  payload: {
    PinnedChat: PinnedChats;
  };
  onSuccess?: () => void;
  onError?: (err: string) => void;
}

const useUpdateBrieGPTUser = () => {
  const { gptUser } = useGPTUser();
  const currentPinnedChats = gptUser?.PinnedChats || [];
  const queryClient = useQueryClient();

  const updateGPTUser = useMutation({
    mutationKey: ["UpdateGPTUser"],
    mutationFn: async ({ payload }: Props) => {
      return BrieConversationAPI.updateBrieGPTUser(payload).then((res) => {
        return res.data.data;
      });
    },
    onSuccess: (_, { payload, onSuccess }) => {
      queryClient.setQueryData(["gptUser"], () => {
        const newPinnedChats = calculatePinnedChats(
          currentPinnedChats,
          payload.PinnedChat
        );

        return {
          ...gptUser,
          PinnedChats: newPinnedChats,
        };
      });
      onSuccess?.();
    },
    onError: (err, { onError }) => {
      if (typeof err === "string" && err === "Unauthorized") return;

      onError?.("An unexpected error occurred. Please try again.");
    },
  });

  function calculatePinnedChats(
    currentPinnedChats: PinnedChats[],
    newPinnedChat: PinnedChats
  ) {
    const isPinned = currentPinnedChats.some(
      (chat: any) => chat.Chat.id === newPinnedChat.Chat.id
    );

    if (isPinned) {
      return currentPinnedChats.filter(
        (chat: any) => chat.Chat.id !== newPinnedChat.Chat.id
      );
    } else {
      return [...currentPinnedChats, newPinnedChat];
    }
  }

  return {
    updateGPTUser: updateGPTUser.mutate,
    updatingGPTUser: updateGPTUser.isPending,
    error: updateGPTUser.error,
  };
};

export default useUpdateBrieGPTUser;
